// stores/appSettingStore.js
'use client';

import { useMemo } from 'react';
import useSWR, { mutate } from 'swr';
import FilterBuilder from '../utils/filterBuilder';
import { fleetApi } from '../api/axiosClient';

const getTzParam = () => `?tz=${Math.abs(new Date().getTimezoneOffset() / 60)}`;

const fetcher = async ([url, payloadStr]) => {
  try {
    const payload = typeof payloadStr === 'string' ? JSON.parse(payloadStr) : payloadStr;
    const response = await fleetApi.post(url + getTzParam(), payload);
    return response;
  } catch (err) {
    console.error('API fetcher error:', { url, payloadStr, status: err?.response?.status, data: err?.response?.data });
    throw err;
  }
};

export const revalidateAppSettingDataList = () => {
  mutate((key) => Array.isArray(key) && key[0] === '/app-setting/search');
};

// Hook for fetching list using SWR & FilterBuilder (Search Multiple Record)
export const useAppSettingDataList = (page = 0, rowsPerPage = 1000, searchKey = 'name', searchValue = '') => {
  const start = page * rowsPerPage;

  const tableName = searchKey === 'company' ? 'm_company' : null;
  const filterLike = searchKey === 'company' ? 'name' : searchKey;

  const payload = useMemo(() => {
    const fb = new FilterBuilder().setPaging(start, rowsPerPage).setOrder(['id']).addJoin('company', ['name'], false);
    if (searchValue && searchValue.trim() !== '') {
      fb.like(filterLike, searchValue, 'AND', tableName);
    }
    return fb.build();
  }, [start, rowsPerPage, searchValue, filterLike, tableName]);

  const key = useMemo(() => ['/app-setting/search', JSON.stringify(payload)], [payload]);

  const { data, error, isLoading } = useSWR(key, fetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    shouldRetryOnError: false,
    keepPreviousData: true,
    revalidateOnMount: true
  });

  return {
    data: data?.data?.data ?? data?.data ?? [],
    totalRecord: data?.data?.totalRecord ?? data?.data?.totalRecords ?? data?.data?.dataSize ?? data?.totalRecord ?? 0,
    isLoading,
    isError: error
  };
};

// GET /app-setting/{id}
export const getAppSettingById = async (id) => {
  if (id == null) throw new Error('id is required');
  try {
    const resp = await fleetApi.get(`/app-setting/${id}${getTzParam()}`);
    return resp?.data ?? null;
  } catch (err) {
    console.error('Failed to fetch app-setting by id:', err);
    throw err;
  }
};

// POST /app-setting/new
export const createAppSetting = async (formData = {}) => {
  try {
    const response = await fleetApi.post(`/app-setting/new${getTzParam()}`, formData);

    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateAppSettingDataList();
    return response.data;
  } catch (err) {
    console.error('Error creating app-setting:', err);
    throw err;
  }
};

// PUT /app-setting/edit/{id}
export const updateAppSetting = async (id, formData = {}) => {
  if (id == null) throw new Error('id is required');
  try {
    const response = await fleetApi.put(`/app-setting/edit/${id}${getTzParam()}`, formData);

    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateAppSettingDataList();
    mutate(`/app-setting/${id}`);
    return response.data;
  } catch (err) {
    console.error('Error updating app-setting:', err);
    throw err;
  }
};

// DELETE /app-setting/delete/{id}
export const deleteAppSetting = async (id) => {
  if (id == null) throw new Error('id is required');
  try {
    const response = await fleetApi.delete(`/app-setting/delete/${id}${getTzParam()}`);

    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateAppSettingDataList();
    return response.data;
  } catch (err) {
    console.error('Error deleting app-setting:', err);
    throw err;
  }
};

// POST /app-setting/uploadfile/{id}
export const uploadAppSettingImage = async (id, file, fieldName = 'file') => {
  if (id == null) throw new Error('id is required');
  if (!file) throw new Error('file is required');
  try {
    const fd = new FormData();
    fd.append(fieldName, file);
    const response = await fleetApi.post(`/app-setting/uploadfile/${id}`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  } catch (error) {
    console.error('Error uploading app-setting image:', error?.response?.data ?? error);
    throw error;
  }
};

// GET /app-setting/images/{id}
export const getAppSettingImage = async (id) => {
  if (id == null) throw new Error('id is required');
  try {
    const response = await fleetApi.get(`/app-setting/images/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching app-setting image:', error?.response?.data ?? error);
    throw error;
  }
};

// DELETE /app-setting/images/{id}
export const deleteAppSettingImage = async (id, type, fileName) => {
  if (id == null) throw new Error('id is required');
  try {
    const params = new URLSearchParams();
    if (type) params.append('type', type);
    if (fileName) params.append('file', fileName);
    const tzParam = getTzParam();
    const queryStr = params.toString() ? `?${params.toString()}&${tzParam.replace('?', '')}` : tzParam;

    const response = await fleetApi.delete(`/app-setting/images/${id}${queryStr}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting app-setting image:', error?.response?.data ?? error);
    throw error;
  }
};
